/**
 * Menu Editor custom-component settings — enum values and config shape shared by
 * the Business Dashboard (editor) and Bloom (storefront). The API owns storage
 * and defaults. See `docs/features/menu-editor-custom-components.md`.
 */
import type { FontFamily } from './fontFamily';
import type { MenuTab } from './menuTab';

export const SearchPositionEnum = [
  'split',
  'header',
  'content_header',
  'filters',
] as const;
export type SearchPosition = (typeof SearchPositionEnum)[number];

export const SEARCH_POSITION = {
  split: 'split',
  header: 'header',
  content_header: 'content_header',
  filters: 'filters',
} as const satisfies Record<string, SearchPosition>;

export interface SearchConfig {
  position: SearchPosition;
}

export const ProductCardSizeEnum = ['small', 'medium', 'large'] as const;
export type ProductCardSize = (typeof ProductCardSizeEnum)[number];

export const ProductCardCtaStyleEnum = [
  'card_bottom_full',
  'card_bottom',
  'image_top',
  'image_bottom',
] as const;
export type ProductCardCtaStyle = (typeof ProductCardCtaStyleEnum)[number];

export const ProductCardCtaIconEnum = ['add', 'bag'] as const;
export type ProductCardCtaIcon = (typeof ProductCardCtaIconEnum)[number];

export const ProductCardHoverEnum = [
  'image_border',
  'image_zoom',
  'shadow',
  'shadow_color',
] as const;
export type ProductCardHover = (typeof ProductCardHoverEnum)[number];

export interface ProductCardsConfig {
  card_border: boolean;
  card_size: ProductCardSize;
  cta_icon: ProductCardCtaIcon; // ignored when cta_style is card_bottom_full
  cta_style: ProductCardCtaStyle;
  hover: ProductCardHover;
}

/** `icon` was formerly called "stacked". */
export const CategoryTabStyleEnum = [
  'icon',
  'pill',
  'tile',
  'tab',
  'image',
] as const;
export type CategoryTabStyle = (typeof CategoryTabStyleEnum)[number];

export const CategoryTabPositionEnum = [
  'above_carousel',
  'below_carousel',
] as const;
export type CategoryTabPosition = (typeof CategoryTabPositionEnum)[number];

export interface CategoryTabsConfig {
  position: CategoryTabPosition;
  style: CategoryTabStyle;
}

/** A single editable color. `contrast` is derived at save time, not edited. */
export interface MenuColorConfig {
  main?: string;
}

export interface MenuColorsConfig {
  deals?: MenuColorConfig;
  header?: MenuColorConfig;
  lineage?: {
    cbd?: MenuColorConfig;
    hybrid?: MenuColorConfig;
    indica?: MenuColorConfig;
    sativa?: MenuColorConfig;
  };
  primary?: MenuColorConfig;
  secondary?: MenuColorConfig;
}

export type CategoryImageKey = Exclude<MenuTab, 'all-products'>;
export type CategoryImagesConfig = {
  [key in CategoryImageKey]?: string;
};

export interface MenuComponentConfig {
  category_images: CategoryImagesConfig;
  category_tabs: CategoryTabsConfig;
  colors: MenuColorsConfig;
  fonts: {
    body: FontFamily | undefined;
    heading: FontFamily | undefined;
  };
  logo?: string | null;
  product_cards: ProductCardsConfig;
  search: SearchConfig;
}
